import React, { useState, useEffect } from "react";
import Select from "../../Elements/Select/Select";
import MainInput from "../../Elements/MainInput/MainInput";
import copyIcon from "../../../assets/copy-alt.png";

import { fetchQrCodeSvg, fetchInstallationInstructions } from "../../../services/EsimService";
import "./InstallationGuide.css";

const INSTALL_METHOD_OPTIONS = [
  { value: "qr", label: "QR code" },
  { value: "manual", label: "Manual" },
];

const DEVICE_TYPE_OPTIONS = [
  { value: "IOS", label: "iPhone (iOS)" },
  { value: "android", label: "Android" },
  { value: "samsung", label: "Samsung Galaxy" },
  { value: "pixel", label: "Google Pixel" },
  { value: "xiaomi", label: "Xiaomi" },
];

const InstallationGuide = ({ selectedEsim, deviceType, setDeviceType }) => {
  const [installMethod, setInstallMethod] = useState("qr");
  const [qrSvg, setQrSvg] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [loadingInstructions, setLoadingInstructions] = useState(false);

  const [installationSteps, setInstallationSteps] = useState([]);
  const [activationSteps, setActivationSteps] = useState([]);

  const parseActivationCode = (code) => {
    if (!code) return { smdp: "", activation: "", lpa: "" };
    const parts = code.split("$");
    return {
      smdp: parts[1] || "",
      activation: parts[2] || "",
      lpa: code,
    };
  };

  const { smdp, activation, lpa } = parseActivationCode(selectedEsim?.activation_code);

  const getDevicePrefix = (deviceType) => {
    return deviceType.toUpperCase() === "IOS" ? "IOS" : deviceType.toLowerCase();
  };

  useEffect(() => {
    if (deviceType && installMethod) {
      setLoadingInstructions(true);
      fetchInstallationInstructions(deviceType, installMethod)
        .then((data) => {
          const typePrefix = getDevicePrefix(deviceType); // "IOS" lub "samsung"
          const methodSuffix = installMethod === "qr" ? "Qr" : "Manual";

          const installationKey = `${typePrefix}${methodSuffix}InstallationSteps`;
          const activationKey = `${typePrefix}${methodSuffix}ActivationSteps`;

          // console.log("Smart prefix:", typePrefix);
          // console.log("Keys:", { installationKey, activationKey });

          setInstallationSteps(data?.[installationKey] || []);
          setActivationSteps(data?.[activationKey] || []);
        })
        .finally(() => setLoadingInstructions(false));
    }
  }, [deviceType, installMethod]);

  useEffect(() => {
    if (installMethod === "qr" && selectedEsim?.iccid) {
      fetchQr(selectedEsim.iccid);
    } else {
      setQrSvg(null);
    }
  }, [installMethod, selectedEsim]);

  const renderStep = (step) => {
    return step.replace(/\*(.*?)\*/g, '<span style="font-weight:600">$1</span>');
  };

  const fetchQr = async (iccid) => {
    setLoadingQr(true);
    try {
      const svg = await fetchQrCodeSvg(iccid);
      let cleaned = svg
        .replace(/svg:/g, "")
        .replace(/xmlns:svg="[^"]*"/g, "")
        .replace(/fill_color="[^"]*"/g, "")
        .replace(/back_color="[^"]*"/g, "")
        .replace(/width="37mm"/, 'width="37"')
        .replace(/height="37mm"/, 'height="37"')
        .replace(/([="\s])([0-9.]+)mm([\s">])/g, "$1$2$3");

      if (!/viewBox=/.test(cleaned)) {
        cleaned = cleaned.replace(/<svg([^>]*)>/, '<svg$1 viewBox="0 0 37 37">');
      }
      setQrSvg(cleaned.trim());
    } catch (error) {
      console.error("QR ERROR:", error);
    } finally {
      setLoadingQr(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="installation-guide">
      <div className="installation-selects-row">
        <Select label="Method" value={installMethod} onChange={setInstallMethod} options={INSTALL_METHOD_OPTIONS} />
        <Select label="Device type" value={deviceType} onChange={setDeviceType} options={DEVICE_TYPE_OPTIONS} />
      </div>

      <div className="installation-main">
        <div className="content-left">
          {installMethod === "qr" ? (
            <div className="qr-container">
              {loadingQr ? (
                <div className="loading-qr">Generating QR code...</div>
              ) : qrSvg ? (
                <>
                  <div className="qr-svg" dangerouslySetInnerHTML={{ __html: qrSvg }} />
                  <div className="qr-description">
                    <span>
                      Scan this QR code to install the eSIM. Ensure a stable internet connection.
                      <span style={{ fontWeight: 600 }}> Please remember to enable roaming.</span>
                    </span>
                  </div>
                </>
              ) : (
                <div className="error-qr">Failed to load QR code</div>
              )}
            </div>
          ) : (
            <div className="manual-container">
              <div className="manual-field">
                <label>SM-DP+ Address</label>
                <div className="input-with-copy">
                  <MainInput value={smdp} readOnly placeholder="rsp-eu.simlessly.com" />
                  <button
                    className={`copy-btn ${copiedField === "smdp" ? "copied" : ""}`}
                    onClick={() => copyToClipboard(smdp, "smdp")}
                    title="Copy to clipboard"
                  >
                    <img src={copyIcon} alt="Copy" />
                  </button>
                </div>
              </div>

              <div className="manual-field">
                <label>Activation Code</label>
                <div className="input-with-copy">
                  <MainInput value={activation} readOnly placeholder="5A11EE53772346F0ABE57D914A883743" />
                  <button
                    className={`copy-btn ${copiedField === "activation" ? "copied" : ""}`}
                    onClick={() => copyToClipboard(activation, "activation")}
                    title="Copy to clipboard"
                  >
                    <img src={copyIcon} alt="Copy" />
                  </button>
                </div>
              </div>

              <div className="manual-field">
                <label>Full LPA Code</label>
                <div className="input-with-copy">
                  <MainInput value={lpa} readOnly placeholder="LPA:1$rsp-eu.simlessly.com$..." />
                  <button
                    className={`copy-btn ${copiedField === "lpa" ? "copied" : ""}`}
                    onClick={() => copyToClipboard(lpa, "lpa")}
                    title="Copy to clipboard"
                  >
                    <img src={copyIcon} alt="Copy" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="content-right">
          <div className="instructions-panel">
            {loadingInstructions ? (
              <div className="loading-instructions">Loading instructions...</div>
            ) : (
              <>
                {/* INSTALACJA */}
                {installationSteps.length > 0 && (
                  <div className="instruction-section">
                    <h5>Installation</h5>
                    <ol className="instruction-steps">
                      {installationSteps.map((step, index) => (
                        <li key={`install-${index}`} dangerouslySetInnerHTML={{ __html: renderStep(step) }} />
                      ))}
                    </ol>
                  </div>
                )}

                {/* AKTYWACJA */}
                {activationSteps.length > 0 && (
                  <div className="instruction-section">
                    <h5>Activation</h5>
                    <ol className="instruction-steps">
                      {activationSteps.map((step, index) => (
                        <li key={`activate-${index}`} dangerouslySetInnerHTML={{ __html: renderStep(step) }} />
                      ))}
                    </ol>
                  </div>
                )}

                {!installationSteps.length && !activationSteps.length && (
                  <div className="no-instructions">No instructions available</div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationGuide;
